import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { 
  getSiteSettings, updateSiteSettings, 
  getAdminPreviousEvents, createPreviousEvent, updatePreviousEvent, deletePreviousEvent,
  getAdminGalleryImages, createGalleryImage, updateGalleryImage, deleteGalleryImage,
  getAdminGalleryVideos, createGalleryVideo, updateGalleryVideo, deleteGalleryVideo,
  getAdminPackages, createPackage, updatePackage, deletePackage,
  getAdminPartners, createPartner, updatePartner, deletePartner
} from '../../api/client';
import { useSettings } from '../../contexts/SettingsContext';
import { toast } from 'sonner';
import { 
  Save, Upload, Eye, Trash2, Plus, Edit2, Image, 
  Layout, Clock, MapPin, FileText, Mail, Palette, Menu, Award, Calendar, Video, Package, Users, Settings
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback image for broken images
const FALLBACK_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiB2aWV3Qm94PSIwIDAgMjAwIDIwMCI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNlNWU3ZWIiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzljYTNhZiIgZm9udC1zaXplPSIxNCI+SW1hZ2Ugbm90IGZvdW5kPC90ZXh0Pjwvc3ZnPg==';

// Reusable Image Upload Component with error handling and multiple upload support
const ImageUpload = ({ value, onChange, label, height = 'h-32', aspectRatio = 'auto', objectFit = 'cover', multiple = false }) => {
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Single file validation
    if (!multiple) {
      const file = files[0];
      if (file.size > 20 * 1024 * 1024) {
        toast.error('File must be less than 20MB');
        return;
      }
    }

    setUploading(true);
    setImageError(false);

    try {
      const token = sessionStorage.getItem('adminToken');
      
      if (multiple && files.length > 1) {
        // Multiple upload
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          if (files[i].size > 20 * 1024 * 1024) {
            toast.error(`File ${files[i].name} is too large (max 20MB)`);
            continue;
          }
          formData.append('files', files[i]);
        }
        
        const response = await axios.post(`${BACKEND_URL}/api/admin/upload-multiple`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
        });
        
        if (response.data.uploaded && response.data.uploaded.length > 0) {
          // Return array of URLs for multiple uploads
          const urls = response.data.uploaded.map(u => u.url);
          onChange(urls);
          toast.success(`${response.data.total_uploaded} images uploaded!`);
        }
        if (response.data.errors && response.data.errors.length > 0) {
          response.data.errors.forEach(err => toast.error(`${err.filename}: ${err.error}`));
        }
      } else {
        // Single upload
        const formData = new FormData();
        formData.append('file', files[0]);
        
        const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
        });
        
        // Backend now returns absolute URL directly
        onChange(response.data.url);
        toast.success('Image uploaded!');
      }
    } catch (error) {
      toast.error('Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const handleRetryUpload = () => {
    setImageError(false);
    onChange('');
  };

  return (
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <div className="mt-1.5">
        <label className="block cursor-pointer">
          <div className={`border-2 border-dashed rounded-lg ${height} flex items-center justify-center overflow-hidden ${
            uploading ? 'border-gray-300 bg-gray-50' : imageError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-green-500 hover:bg-green-50'
          }`} style={{ aspectRatio }}>
            {uploading ? (
              <div className="animate-spin h-6 w-6 border-2 border-green-600 border-t-transparent rounded-full"></div>
            ) : value && !imageError ? (
              <img 
                src={value} 
                alt={label} 
                className={`max-w-full max-h-full object-${objectFit}`}
                onError={handleImageError}
              />
            ) : imageError ? (
              <div className="text-center text-red-400 p-2">
                <Image size={24} className="mx-auto mb-1" />
                <span className="text-xs block">Image not found</span>
                <button 
                  onClick={(e) => { e.preventDefault(); handleRetryUpload(); }}
                  className="text-xs text-blue-500 hover:underline mt-1"
                >
                  Click to replace
                </button>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <Upload size={24} className="mx-auto mb-1" />
                <span className="text-xs">{multiple ? 'Click to upload (multiple)' : 'Click to upload'}</span>
              </div>
            )}
          </div>
          <input 
            type="file" 
            accept="image/*,.heic,.heif" 
            onChange={handleUpload} 
            className="hidden" 
            disabled={uploading}
            multiple={multiple}
          />
        </label>
        {value && !imageError && (
          <button onClick={() => onChange('')} className="text-xs text-red-500 mt-1 hover:underline">Remove</button>
        )}
      </div>
    </div>
  );
};

// PDF Upload Component
const PDFUpload = ({ value, onChange, label }) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = sessionStorage.getItem('adminToken');
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      // Backend now returns absolute URL directly
      onChange(response.data.url);
      toast.success('File uploaded!');
    } catch (error) {
      toast.error('Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <div className="mt-1.5">
        <label className="block cursor-pointer">
          <div className={`border-2 border-dashed rounded-lg p-4 flex items-center justify-center ${
            uploading ? 'border-gray-300 bg-gray-50' : 'border-gray-300 hover:border-green-500 hover:bg-green-50'
          }`}>
            {uploading ? (
              <span className="text-sm text-gray-500">Uploading...</span>
            ) : value ? (
              <div className="flex items-center gap-2 text-green-600">
                <FileText size={20} />
                <span className="text-sm">PDF Uploaded</span>
              </div>
            ) : (
              <div className="text-center text-gray-400">
                <Upload size={20} className="mx-auto mb-1" />
                <span className="text-xs">Upload PDF</span>
              </div>
            )}
          </div>
          <input type="file" accept=".pdf" onChange={handleUpload} className="hidden" disabled={uploading} />
        </label>
        {value && (
          <div className="flex gap-2 mt-1">
            <a href={value} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline">View</a>
            <button onClick={() => onChange('')} className="text-xs text-red-500 hover:underline">Remove</button>
          </div>
        )}
      </div>
    </div>
  );
};

export const VisualEditor = () => {
  const { refreshSettings } = useSettings();
  const [settings, setSettings] = useState({});
  const [previousEvents, setPreviousEvents] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [packages, setPackages] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  
  // Dialog states
  const [eventDialog, setEventDialog] = useState({ open: false, event: null });
  const [galleryDialog, setGalleryDialog] = useState({ open: false, image: null });
  const [videoDialog, setVideoDialog] = useState({ open: false, video: null });
  const [packageDialog, setPackageDialog] = useState({ open: false, pkg: null });
  const [partnerDialog, setPartnerDialog] = useState({ open: false, partner: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [settingsData, eventsData, imagesData] = await Promise.all([
        getSiteSettings(),
        getAdminPreviousEvents(),
        getAdminGalleryImages()
      ]);
      
      // Load videos, packages, and partners separately (they might not exist yet)
      let videosData = { videos: [] };
      let packagesData = { packages: [] };
      let partnersData = { partners: [] };
      try {
        videosData = await getAdminGalleryVideos();
      } catch { /* non-critical */ }
      try {
        packagesData = await getAdminPackages();
      } catch { /* non-critical */ }
      try {
        partnersData = await getAdminPartners();
      } catch { /* non-critical */ }
      
      setSettings(settingsData);
      setPreviousEvents(eventsData.events || []);
      setGalleryImages(imagesData.images || []);
      setGalleryVideos(videosData.videos || []);
      setPackages(packagesData.packages || []);
      setPartners(partnersData.partners || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (key, value) => {
    const keys = key.split('.');
    const newSettings = JSON.parse(JSON.stringify(settings));
    let current = newSettings;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setSettings(newSettings);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateSiteSettings(settings);
      await refreshSettings();
      toast.success('Settings saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Previous Events handlers
  const handleSaveEvent = async (eventData) => {
    try {
      if (eventData.id) {
        await updatePreviousEvent(eventData.id, eventData);
        toast.success('Event updated!');
      } else {
        await createPreviousEvent(eventData);
        toast.success('Event created!');
      }
      const data = await getAdminPreviousEvents();
      setPreviousEvents(data.events || []);
      setEventDialog({ open: false, event: null });
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await deletePreviousEvent(id);
      setPreviousEvents(previousEvents.filter(e => e.id !== id));
      toast.success('Event deleted!');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Gallery handlers
  const handleSaveGalleryImage = async (imageData) => {
    try {
      if (imageData.id) {
        await updateGalleryImage(imageData.id, imageData);
        toast.success('Image updated!');
      } else {
        await createGalleryImage(imageData);
        toast.success('Image added!');
      }
      const data = await getAdminGalleryImages();
      setGalleryImages(data.images || []);
      setGalleryDialog({ open: false, image: null });
    } catch (error) {
      toast.error('Failed to save image');
    }
  };

  const handleBulkUploadGallery = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const token = sessionStorage.getItem('adminToken');
    const formData = new FormData();
    
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > 20 * 1024 * 1024) {
        toast.error(`${files[i].name} is too large (max 20MB)`);
        continue;
      }
      formData.append('files', files[i]);
    }
    
    if (formData.getAll('files').length === 0) {
      toast.error('No valid files to upload');
      return;
    }
    
    toast.info(`Uploading ${formData.getAll('files').length} images...`);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/upload-multiple`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.uploaded && response.data.uploaded.length > 0) {
        let created = 0;
        for (const uploaded of response.data.uploaded) {
          try {
            await createGalleryImage({
              src: uploaded.url,
              alt: uploaded.original_filename || 'Gallery image',
              caption: ''
            });
            created++;
          } catch {
            // Failed to create gallery entry for this image
          }
        }
        
        const data = await getAdminGalleryImages();
        setGalleryImages(data.images || []);
        toast.success(`${created} images added to gallery!`);
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach(err => toast.error(`${err.filename}: ${err.error}`));
      }
    } catch {
      toast.error('Failed to upload images');
    }
    
    e.target.value = '';
  };

  const handleDeleteGalleryImage = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await deleteGalleryImage(id);
      setGalleryImages(galleryImages.filter(i => i.id !== id));
      toast.success('Image deleted!');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Video handlers
  const handleSaveVideo = async (videoData) => {
    try {
      if (videoDialog.video) {
        await updateGalleryVideo(videoDialog.video.id, videoData);
        toast.success('Video updated!');
      } else {
        await createGalleryVideo(videoData);
        toast.success('Video added!');
      }
      const data = await getAdminGalleryVideos();
      setGalleryVideos(data.videos || []);
      setVideoDialog({ open: false, video: null });
    } catch (error) {
      toast.error('Failed to save video');
    }
  };

  const handleDeleteVideo = async (id) => {
    if (!window.confirm('Delete this video?')) return;
    try {
      await deleteGalleryVideo(id);
      setGalleryVideos(galleryVideos.filter(v => v.id !== id));
      toast.success('Video deleted!');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Package handlers
  const handleSavePackage = async (pkgData) => {
    try {
      if (packageDialog.pkg) {
        await updatePackage(packageDialog.pkg.id, pkgData);
        toast.success('Package updated!');
      } else {
        await createPackage(pkgData);
        toast.success('Package added!');
      }
      const data = await getAdminPackages();
      setPackages(data.packages || []);
      setPackageDialog({ open: false, pkg: null });
    } catch (error) {
      toast.error('Failed to save package');
    }
  };

  const handleDeletePackage = async (id) => {
    if (!window.confirm('Delete this package?')) return;
    try {
      await deletePackage(id);
      setPackages(packages.filter(p => p.id !== id));
      toast.success('Package deleted!');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Partner handlers
  const handleSavePartner = async (partnerData) => {
    try {
      if (partnerDialog.partner) {
        await updatePartner(partnerDialog.partner.id, partnerData);
        toast.success('Partner updated!');
      } else {
        await createPartner(partnerData);
        toast.success('Partner added!');
      }
      const data = await getAdminPartners();
      setPartners(data.partners || []);
      setPartnerDialog({ open: false, partner: null });
    } catch (error) {
      toast.error('Failed to save partner');
    }
  };

  const handleDeletePartner = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    try {
      await deletePartner(id);
      setPartners(partners.filter(p => p.id !== id));
      toast.success('Partner deleted!');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin h-12 w-12 border-4 border-green-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-50">
        <h1 className="text-2xl font-bold">Visual Editor</h1>
        <Button onClick={handleSaveSettings} disabled={saving} className="bg-green-600 hover:bg-green-700">
          <Save size={16} className="mr-2" />
          {saving ? 'Saving...' : 'Save All Settings'}
        </Button>
      </div>

      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="hero"><Layout size={16} className="mr-1" />Hero & Logo</TabsTrigger>
            <TabsTrigger value="countdown"><Clock size={16} className="mr-1" />Countdown</TabsTrigger>
            <TabsTrigger value="stats"><Award size={16} className="mr-1" />Stats</TabsTrigger>
            <TabsTrigger value="about"><FileText size={16} className="mr-1" />About</TabsTrigger>
            <TabsTrigger value="destinations"><MapPin size={16} className="mr-1" />Destinations</TabsTrigger>
            <TabsTrigger value="packages"><Package size={16} className="mr-1" />Packages</TabsTrigger>
            <TabsTrigger value="events"><Calendar size={16} className="mr-1" />Previous Events</TabsTrigger>
            <TabsTrigger value="gallery"><Image size={16} className="mr-1" />Photos</TabsTrigger>
            <TabsTrigger value="videos"><Video size={16} className="mr-1" />Videos</TabsTrigger>
            <TabsTrigger value="partners"><Users size={16} className="mr-1" />Partners</TabsTrigger>
            <TabsTrigger value="brochure"><FileText size={16} className="mr-1" />Brochure</TabsTrigger>
            <TabsTrigger value="contact"><Mail size={16} className="mr-1" />Contact</TabsTrigger>
            <TabsTrigger value="footer"><Menu size={16} className="mr-1" />Footer</TabsTrigger>
            <TabsTrigger value="colors"><Palette size={16} className="mr-1" />Colors</TabsTrigger>
            <TabsTrigger value="controls"><Settings size={16} className="mr-1" />Site Controls</TabsTrigger>
          </TabsList>

          {/* Hero & Logo Tab */}
          <TabsContent value="hero">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle>Logo</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <ImageUpload 
                    value={settings.nav?.logoImage} 
                    onChange={(v) => updateSetting('nav.logoImage', v)} 
                    label="Logo Image (Upload your logo)"
                    height="h-20"
                    aspectRatio="3/1"
                    objectFit="contain"
                  />
                  <p className="text-xs text-gray-500">Recommended: PNG with transparent background, max height 100px</p>
                  <div>
                    <Label>Logo Text (Fallback if no image)</Label>
                    <Input value={settings.nav?.logoText || ''} onChange={(e) => updateSetting('nav.logoText', e.target.value)} placeholder="ASEAN Business Forum" className="mt-1" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Hero Background</CardTitle></CardHeader>
                <CardContent>
                  <ImageUpload 
                    value={settings.heroImage} 
                    onChange={(v) => updateSetting('heroImage', v)} 
                    label="Hero Background Image"
                    height="h-48"
                  />
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader><CardTitle>Hero Content</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Location Tag</Label>
                      <Input value={settings.heroLocation || ''} onChange={(e) => updateSetting('heroLocation', e.target.value)} placeholder="Batam & Bintan" className="mt-1" />
                    </div>
                    <div>
                      <Label>Year Tag</Label>
                      <Input value={settings.heroYear || ''} onChange={(e) => updateSetting('heroYear', e.target.value)} placeholder="2026" className="mt-1" />
                    </div>
                    <div>
                      <Label>Feature Tag</Label>
                      <Input value={settings.heroFeature || ''} onChange={(e) => updateSetting('heroFeature', e.target.value)} placeholder="B2B Matchmaking" className="mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input value={settings.heroTitle || ''} onChange={(e) => updateSetting('heroTitle', e.target.value)} placeholder="ASEAN Business Forum" className="mt-1" />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Textarea value={settings.heroSubtitle || ''} onChange={(e) => updateSetting('heroSubtitle', e.target.value)} placeholder="A global platform..." rows={2} className="mt-1" />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Primary Button Text</Label>
                      <Input value={settings.heroCtaPrimary || ''} onChange={(e) => updateSetting('heroCtaPrimary', e.target.value)} placeholder="Become an Exhibitor" className="mt-1" />
                    </div>
                    <div>
                      <Label>Secondary Button Text</Label>
                      <Input value={settings.heroCtaSecondary || ''} onChange={(e) => updateSetting('heroCtaSecondary', e.target.value)} placeholder="View Brochure" className="mt-1" />
                    </div>
                  </div>
                  
                  {/* Sub-Event Themes */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-gray-700 mb-3">Sub-Event Themes (editable each year)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Sub-Event Theme 1</Label>
                        <Input value={settings.subEventTheme1 || ''} onChange={(e) => updateSetting('subEventTheme1', e.target.value)} placeholder="Halal Business Forum" className="mt-1" />
                      </div>
                      <div>
                        <Label>Sub-Event Theme 2</Label>
                        <Input value={settings.subEventTheme2 || ''} onChange={(e) => updateSetting('subEventTheme2', e.target.value)} placeholder="Women Entrepreneurs Summit" className="mt-1" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">These appear as badges below the main title. Change them each year without touching the main ASEAN Business Forum brand.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Stats Tab */}
          <TabsContent value="stats">
            <Card>
              <CardHeader><CardTitle>Statistics</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label>Exhibitors Count</Label>
                    <Input value={settings.stats?.exhibitors || ''} onChange={(e) => updateSetting('stats.exhibitors', e.target.value)} placeholder="300+" />
                    <Label>Label</Label>
                    <Input value={settings.stats?.exhibitorsLabel || ''} onChange={(e) => updateSetting('stats.exhibitorsLabel', e.target.value)} placeholder="Exhibitors" />
                  </div>
                  <div className="space-y-2">
                    <Label>Countries Count</Label>
                    <Input value={settings.stats?.countries || ''} onChange={(e) => updateSetting('stats.countries', e.target.value)} placeholder="50+" />
                    <Label>Label</Label>
                    <Input value={settings.stats?.countriesLabel || ''} onChange={(e) => updateSetting('stats.countriesLabel', e.target.value)} placeholder="Countries" />
                  </div>
                  <div className="space-y-2">
                    <Label>Delegations Count</Label>
                    <Input value={settings.stats?.delegations || ''} onChange={(e) => updateSetting('stats.delegations', e.target.value)} placeholder="150+" />
                    <Label>Label</Label>
                    <Input value={settings.stats?.delegationsLabel || ''} onChange={(e) => updateSetting('stats.delegationsLabel', e.target.value)} placeholder="Trade Delegations" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Countdown Tab */}
          <TabsContent value="countdown">
            <Card>
              <CardHeader><CardTitle>Event Countdown Settings</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Event Date & Time</Label>
                      <Input 
                        type="datetime-local" 
                        value={settings.eventDate ? settings.eventDate.slice(0, 16) : ''} 
                        onChange={(e) => updateSetting('eventDate', e.target.value ? new Date(e.target.value).toISOString() : '')} 
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Set the date and time when the event begins</p>
                    </div>
                    <div>
                      <Label>Countdown Title</Label>
                      <Input 
                        value={settings.countdown?.title || ''} 
                        onChange={(e) => updateSetting('countdown.title', e.target.value)} 
                        placeholder="EVENT COUNTDOWN" 
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Countdown Subtitle</Label>
                      <Input 
                        value={settings.countdown?.subtitle || ''} 
                        onChange={(e) => updateSetting('countdown.subtitle', e.target.value)} 
                        placeholder="Event Begins: 19 June 2026" 
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-gray-900 text-white p-6 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-400 mb-2">Preview</h4>
                      <p className="text-lg font-bold mb-1">{settings.countdown?.title || 'EVENT COUNTDOWN'}</p>
                      <p className="text-sm text-gray-400 mb-4">{settings.countdown?.subtitle || 'Event Begins: 19 June 2026'}</p>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        {['Days', 'Hours', 'Minutes', 'Seconds'].map((unit, idx) => (
                          <div key={unit} className="bg-gray-800 p-2 rounded">
                            <div className="text-2xl font-bold text-green-500">{[161, 17, 45, 30][idx]}</div>
                            <div className="text-xs text-gray-400">{unit}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-800 mb-2">ℹ️ Countdown Features</h4>
                      <ul className="text-xs text-blue-700 space-y-1">
                        <li>• Countdown automatically calculates from event date</li>
                        <li>• Shows Days, Hours, Minutes, Seconds</li>
                        <li>• Updates in real-time on the website</li>
                        <li>• Displays feature highlights below countdown</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-6">
                  <h4 className="font-medium mb-4">Countdown Feature Highlights</h4>
                  <p className="text-sm text-gray-500 mb-4">These are the feature cards shown below the countdown timer</p>
                  <div className="grid md:grid-cols-4 gap-4">
                    <div className="border rounded-lg p-4">
                      <div className="text-2xl mb-2">🤝</div>
                      <Input 
                        value={settings.countdown?.feature1Title || ''} 
                        onChange={(e) => updateSetting('countdown.feature1Title', e.target.value)} 
                        placeholder="B2B Matchmaking" 
                        className="mb-2"
                      />
                      <Textarea 
                        value={settings.countdown?.feature1Desc || ''} 
                        onChange={(e) => updateSetting('countdown.feature1Desc', e.target.value)} 
                        placeholder="Curated meetings for meaningful partnerships."
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                    <div className="border rounded-lg p-4">
                      <div className="text-2xl mb-2">🏪</div>
                      <Input 
                        value={settings.countdown?.feature2Title || ''} 
                        onChange={(e) => updateSetting('countdown.feature2Title', e.target.value)} 
                        placeholder="Halal Marketplace" 
                        className="mb-2"
                      />
                      <Textarea 
                        value={settings.countdown?.feature2Desc || ''} 
                        onChange={(e) => updateSetting('countdown.feature2Desc', e.target.value)} 
                        placeholder="Showcase your products to a global audience."
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                    <div className="border rounded-lg p-4">
                      <div className="text-2xl mb-2">🌍</div>
                      <Input 
                        value={settings.countdown?.feature3Title || ''} 
                        onChange={(e) => updateSetting('countdown.feature3Title', e.target.value)} 
                        placeholder="Delegations" 
                        className="mb-2"
                      />
                      <Textarea 
                        value={settings.countdown?.feature3Desc || ''} 
                        onChange={(e) => updateSetting('countdown.feature3Desc', e.target.value)} 
                        placeholder="Connect with official country delegations."
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                    <div className="border rounded-lg p-4">
                      <div className="text-2xl mb-2">🎯</div>
                      <Input 
                        value={settings.countdown?.feature4Title || ''} 
                        onChange={(e) => updateSetting('countdown.feature4Title', e.target.value)} 
                        placeholder="Networking" 
                        className="mb-2"
                      />
                      <Textarea 
                        value={settings.countdown?.feature4Desc || ''} 
                        onChange={(e) => updateSetting('countdown.feature4Desc', e.target.value)} 
                        placeholder="Exclusive events for industry leaders."
                        rows={2}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* About Tab */}
          <TabsContent value="about">
            <Card>
              <CardHeader><CardTitle>About Section</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Section Title</Label>
                  <Input value={settings.about?.title || ''} onChange={(e) => updateSetting('about.title', e.target.value)} placeholder="About the Forum" className="mt-1" />
                </div>
                {['card1', 'card2', 'card3'].map((card, idx) => (
                  <div key={card} className="p-4 bg-gray-50 rounded-lg">
                    <Label className="font-semibold">Card {idx + 1}</Label>
                    <div className="grid md:grid-cols-2 gap-4 mt-2">
                      <div>
                        <Label>Title</Label>
                        <Input value={settings.about?.[card]?.title || ''} onChange={(e) => updateSetting(`about.${card}.title`, e.target.value)} className="mt-1" />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea value={settings.about?.[card]?.description || ''} onChange={(e) => updateSetting(`about.${card}.description`, e.target.value)} rows={2} className="mt-1" />
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Destinations Tab */}
          <TabsContent value="destinations">
            <Card>
              <CardHeader><CardTitle>Destinations</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Section Title</Label>
                  <Input value={settings.destinations?.title || ''} onChange={(e) => updateSetting('destinations.title', e.target.value)} placeholder="Host Destinations" className="mt-1" />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                    <Label className="font-semibold">Batam</Label>
                    <ImageUpload value={settings.destinations?.batam?.image} onChange={(v) => updateSetting('destinations.batam.image', v)} label="Image" />
                    <div>
                      <Label>Title</Label>
                      <Input value={settings.destinations?.batam?.title || ''} onChange={(e) => updateSetting('destinations.batam.title', e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={settings.destinations?.batam?.description || ''} onChange={(e) => updateSetting('destinations.batam.description', e.target.value)} rows={3} className="mt-1" />
                    </div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg space-y-4">
                    <Label className="font-semibold">Bintan</Label>
                    <ImageUpload value={settings.destinations?.bintan?.image} onChange={(v) => updateSetting('destinations.bintan.image', v)} label="Image" />
                    <div>
                      <Label>Title</Label>
                      <Input value={settings.destinations?.bintan?.title || ''} onChange={(e) => updateSetting('destinations.bintan.title', e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={settings.destinations?.bintan?.description || ''} onChange={(e) => updateSetting('destinations.bintan.description', e.target.value)} rows={3} className="mt-1" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Previous Events Tab */}
          <TabsContent value="events">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Previous Events (Our Journey)</CardTitle>
                <Button onClick={() => setEventDialog({ open: true, event: null })} className="bg-green-600 hover:bg-green-700">
                  <Plus size={16} className="mr-1" /> Add Event
                </Button>
              </CardHeader>
              <CardContent>
                {previousEvents.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No previous events. Add your first event!</p>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {previousEvents.map((event) => (
                      <div key={event.id} className="relative border rounded-lg overflow-hidden group">
                        {event.image ? (
                          <img src={event.image} alt={event.year} className="w-full h-32 object-cover" />
                        ) : (
                          <div className="w-full h-32 bg-gray-200 flex items-center justify-center text-gray-400">No Image</div>
                        )}
                        <div className="p-3">
                          <div className="text-xl font-bold text-green-600">{event.year}</div>
                          <div className="font-medium">{event.location}</div>
                          <div className="text-xs text-gray-500">{event.countries}+ Countries • {event.attendees}+ Attendees</div>
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1">
                          <button onClick={() => setEventDialog({ open: true, event })} className="p-1 bg-white rounded shadow"><Edit2 size={14} /></button>
                          <button onClick={() => handleDeleteEvent(event.id)} className="p-1 bg-red-500 text-white rounded shadow"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Event Gallery</CardTitle>
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <Button asChild className="bg-blue-600 hover:bg-blue-700">
                      <span><Upload size={16} className="mr-1" /> Upload Multiple</span>
                    </Button>
                    <input 
                      type="file" 
                      accept="image/*" 
                      multiple 
                      onChange={handleBulkUploadGallery} 
                      className="hidden" 
                      data-testid="gallery-bulk-upload-input"
                    />
                  </label>
                  <Button onClick={() => setGalleryDialog({ open: true, image: null })} className="bg-green-600 hover:bg-green-700" data-testid="gallery-add-single-btn">
                    <Plus size={16} className="mr-1" /> Add Image
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {galleryImages.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                    <Image size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500 mb-2">No gallery images yet</p>
                    <p className="text-sm text-gray-400 mb-4">Upload multiple images at once or add them one by one</p>
                    <label className="cursor-pointer inline-block">
                      <Button asChild className="bg-blue-600 hover:bg-blue-700">
                        <span><Upload size={16} className="mr-1" /> Upload Images</span>
                      </Button>
                      <input 
                        type="file" 
                        accept="image/*" 
                        multiple 
                        onChange={handleBulkUploadGallery} 
                        className="hidden" 
                      />
                    </label>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {galleryImages.map((img) => (
                      <div key={img.id} className="relative group" data-testid={`gallery-image-${img.id}`}>
                        <img src={img.src} alt={img.alt} className="w-full h-24 object-cover rounded-lg" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded-lg flex items-center justify-center gap-2 transition-opacity">
                          <button onClick={() => setGalleryDialog({ open: true, image: img })} className="p-1 bg-white rounded" data-testid={`gallery-edit-${img.id}`}><Edit2 size={14} /></button>
                          <button onClick={() => handleDeleteGalleryImage(img.id)} className="p-1 bg-red-500 text-white rounded" data-testid={`gallery-delete-${img.id}`}><Trash2 size={14} /></button>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 truncate">{img.caption || 'No caption'}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Videos Tab */}
          <TabsContent value="videos">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Video Gallery</CardTitle>
                <Button onClick={() => setVideoDialog({ open: true, video: null })} className="bg-green-600 hover:bg-green-700">
                  <Plus size={16} className="mr-1" /> Add Video
                </Button>
              </CardHeader>
              <CardContent>
                {galleryVideos.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No videos yet. Add your first video!</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {galleryVideos.map((video) => (
                      <div key={video.id} className="relative group bg-gray-100 rounded-lg overflow-hidden">
                        <div className="aspect-video bg-gray-200 flex items-center justify-center">
                          {video.thumbnail ? (
                            <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                          ) : (
                            <Video size={48} className="text-gray-400" />
                          )}
                        </div>
                        <div className="p-3">
                          <p className="font-medium truncate">{video.title || 'Untitled'}</p>
                          <p className="text-xs text-gray-500">{video.type || 'upload'}</p>
                        </div>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1">
                          <button onClick={() => setVideoDialog({ open: true, video })} className="p-1.5 bg-white rounded shadow"><Edit2 size={14} /></button>
                          <button onClick={() => handleDeleteVideo(video.id)} className="p-1.5 bg-red-500 text-white rounded shadow"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Packages</CardTitle>
                <Button onClick={() => setPackageDialog({ open: true, pkg: null })} className="bg-green-600 hover:bg-green-700">
                  <Plus size={16} className="mr-1" /> Add Package
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Section Title (English)</Label>
                    <Input value={settings.packagesTitle?.en || ''} onChange={(e) => updateSetting('packagesTitle.en', e.target.value)} placeholder="Our Packages" className="mt-1" />
                  </div>
                  <div>
                    <Label>Section Subtitle (English)</Label>
                    <Input value={settings.packagesSubtitle?.en || ''} onChange={(e) => updateSetting('packagesSubtitle.en', e.target.value)} placeholder="Choose the perfect package" className="mt-1" />
                  </div>
                </div>
                
                {packages.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No packages yet. Add your first package!</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {packages.map((pkg) => (
                      <div key={pkg.id} className="relative group border rounded-lg p-4 bg-white">
                        <div className="flex items-center gap-2 mb-2">
                          <Package size={20} className="text-green-600" />
                          <span className="font-medium">{pkg.title?.en || pkg.type}</span>
                          {pkg.featured && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Featured</span>}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{pkg.description?.en || 'No description'}</p>
                        <p className="text-xs text-gray-400 mt-2">{pkg.features?.length || 0} features</p>
                        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1">
                          <button onClick={() => setPackageDialog({ open: true, pkg })} className="p-1.5 bg-gray-100 rounded"><Edit2 size={14} /></button>
                          <button onClick={() => handleDeletePackage(pkg.id)} className="p-1.5 bg-red-500 text-white rounded"><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Our Partners</CardTitle>
                <Button onClick={() => setPartnerDialog({ open: true, partner: null })} className="bg-green-600 hover:bg-green-700">
                  <Plus size={16} className="mr-1" /> Add Partner
                </Button>
              </CardHeader>
              <CardContent>
                {partners.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No partners yet. Add your first partner!</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {partners.map((partner) => (
                      <div key={partner.id} className="relative group border rounded-lg p-3 bg-white">
                        <div className="h-20 flex items-center justify-center mb-2">
                          {partner.logo ? (
                            <img src={partner.logo} alt={partner.name} className="max-w-full max-h-full object-contain" />
                          ) : (
                            <div className="text-gray-400 text-center">
                              <Users size={24} className="mx-auto" />
                              <span className="text-xs">{partner.name}</span>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-center font-medium truncate">{partner.name}</p>
                        <p className="text-xs text-center text-gray-500">{partner.category}</p>
                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 flex gap-1">
                          <button onClick={() => setPartnerDialog({ open: true, partner })} className="p-1 bg-white border rounded shadow-sm"><Edit2 size={12} /></button>
                          <button onClick={() => handleDeletePartner(partner.id)} className="p-1 bg-red-500 text-white rounded shadow-sm"><Trash2 size={12} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Brochure Tab */}
          <TabsContent value="brochure">
            <Card>
              <CardHeader><CardTitle>Brochure Section</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Section Title</Label>
                      <Input value={settings.brochure?.title || ''} onChange={(e) => updateSetting('brochure.title', e.target.value)} placeholder="Official Brochure" className="mt-1" />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea value={settings.brochure?.description || ''} onChange={(e) => updateSetting('brochure.description', e.target.value)} rows={3} className="mt-1" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Primary Button</Label>
                        <Input value={settings.brochure?.button || ''} onChange={(e) => updateSetting('brochure.button', e.target.value)} placeholder="Download" className="mt-1" />
                      </div>
                      <div>
                        <Label>Secondary Button</Label>
                        <Input value={settings.brochure?.buttonAlt || ''} onChange={(e) => updateSetting('brochure.buttonAlt', e.target.value)} placeholder="Request Deck" className="mt-1" />
                      </div>
                    </div>
                    <div>
                      <Label>Note</Label>
                      <Input value={settings.brochure?.note || ''} onChange={(e) => updateSetting('brochure.note', e.target.value)} className="mt-1" />
                    </div>
                  </div>
                  <div className="space-y-4">
                    <ImageUpload 
                      value={settings.brochure?.thumbnail} 
                      onChange={(v) => updateSetting('brochure.thumbnail', v)} 
                      label="Brochure Thumbnail/Preview Image"
                      height="h-40"
                      aspectRatio="3/4"
                      objectFit="contain"
                    />
                    <p className="text-xs text-gray-500">Upload a preview image of your brochure cover</p>
                    <PDFUpload value={settings.brochure?.file} onChange={(v) => updateSetting('brochure.file', v)} label="Brochure PDF" />
                    <PDFUpload value={settings.brochure?.sponsorshipFile} onChange={(v) => updateSetting('brochure.sponsorshipFile', v)} label="Sponsorship Deck PDF" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact">
            <Card>
              <CardHeader><CardTitle>Contact Section</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Section Title</Label>
                  <Input value={settings.contact?.title || ''} onChange={(e) => updateSetting('contact.title', e.target.value)} className="mt-1" />
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>WhatsApp Number</Label>
                    <Input value={settings.contact?.whatsapp || ''} onChange={(e) => updateSetting('contact.whatsapp', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={settings.contact?.email || ''} onChange={(e) => updateSetting('contact.email', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input value={settings.contact?.location || ''} onChange={(e) => updateSetting('contact.location', e.target.value)} className="mt-1" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Footer Tab */}
          <TabsContent value="footer">
            <Card>
              <CardHeader><CardTitle>Footer</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Footer Text</Label>
                  <Input value={settings.footer?.text || ''} onChange={(e) => updateSetting('footer.text', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label>Copyright</Label>
                  <Input value={settings.footer?.copyright || ''} onChange={(e) => updateSetting('footer.copyright', e.target.value)} className="mt-1" />
                </div>
                <div className="grid md:grid-cols-4 gap-4">
                  <div><Label>Facebook</Label><Input value={settings.footer?.facebook || ''} onChange={(e) => updateSetting('footer.facebook', e.target.value)} className="mt-1" /></div>
                  <div><Label>Instagram</Label><Input value={settings.footer?.instagram || ''} onChange={(e) => updateSetting('footer.instagram', e.target.value)} className="mt-1" /></div>
                  <div><Label>LinkedIn</Label><Input value={settings.footer?.linkedin || ''} onChange={(e) => updateSetting('footer.linkedin', e.target.value)} className="mt-1" /></div>
                  <div><Label>Twitter/X</Label><Input value={settings.footer?.twitter || ''} onChange={(e) => updateSetting('footer.twitter', e.target.value)} className="mt-1" /></div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Colors Tab */}
          <TabsContent value="colors">
            <Card>
              <CardHeader><CardTitle>Theme Colors</CardTitle></CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Primary Color</Label>
                    <div className="flex gap-3 mt-1">
                      <input type="color" value={settings.colors?.primary || '#0E7A5F'} onChange={(e) => updateSetting('colors.primary', e.target.value)} className="w-12 h-10 rounded border cursor-pointer" />
                      <Input value={settings.colors?.primary || ''} onChange={(e) => updateSetting('colors.primary', e.target.value)} placeholder="#0E7A5F" />
                    </div>
                  </div>
                  <div>
                    <Label>Hover Color</Label>
                    <div className="flex gap-3 mt-1">
                      <input type="color" value={settings.colors?.primaryHover || '#0A5F4A'} onChange={(e) => updateSetting('colors.primaryHover', e.target.value)} className="w-12 h-10 rounded border cursor-pointer" />
                      <Input value={settings.colors?.primaryHover || ''} onChange={(e) => updateSetting('colors.primaryHover', e.target.value)} placeholder="#0A5F4A" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Site Controls Tab */}
          <TabsContent value="controls">
            <div className="space-y-6">
              <Card>
                <CardHeader><CardTitle>Section Visibility</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <label className="flex items-center justify-between cursor-pointer p-3 rounded-lg border hover:bg-gray-50 transition">
                    <div>
                      <p className="font-medium text-gray-900">Women Entrepreneurs Summit</p>
                      <p className="text-sm text-gray-500">Show the Women Entrepreneurs Summit section on the landing page</p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        data-testid="toggle-women-entrepreneurs"
                        checked={settings.showWomenEntrepreneurs !== false}
                        onChange={(e) => updateSetting('showWomenEntrepreneurs', e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${settings.showWomenEntrepreneurs !== false ? 'bg-green-500' : 'bg-gray-300'}`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${settings.showWomenEntrepreneurs !== false ? 'translate-x-5.5 ml-[22px]' : 'ml-0.5'}`} />
                      </div>
                    </div>
                  </label>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Language Settings</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-500 mb-2">Enable or disable languages on the public website. At least one language must remain enabled.</p>
                  {[
                    { code: 'en', label: 'English', flag: '🇬🇧' },
                    { code: 'tr', label: 'Turkish (Türkçe)', flag: '🇹🇷' },
                    { code: 'id', label: 'Indonesian (Bahasa)', flag: '🇮🇩' }
                  ].map(lang => {
                    const enabledList = (settings.enabledLanguages || 'en,tr,id').split(',').map(l => l.trim());
                    const isEnabled = enabledList.includes(lang.code);
                    const isOnlyOne = enabledList.length <= 1 && isEnabled;
                    return (
                      <label key={lang.code} className="flex items-center justify-between cursor-pointer p-3 rounded-lg border hover:bg-gray-50 transition">
                        <div className="flex items-center gap-3">
                          <span className="text-xl">{lang.flag}</span>
                          <div>
                            <p className="font-medium text-gray-900">{lang.label}</p>
                            <p className="text-xs text-gray-400">Code: {lang.code}</p>
                          </div>
                        </div>
                        <div className="relative">
                          <input
                            type="checkbox"
                            data-testid={`toggle-lang-${lang.code}`}
                            checked={isEnabled}
                            disabled={isOnlyOne}
                            onChange={(e) => {
                              let codes = (settings.enabledLanguages || 'en,tr,id').split(',').map(l => l.trim());
                              if (e.target.checked) {
                                if (!codes.includes(lang.code)) codes.push(lang.code);
                              } else {
                                codes = codes.filter(c => c !== lang.code);
                              }
                              if (codes.length === 0) codes = ['en'];
                              updateSetting('enabledLanguages', codes.join(','));
                            }}
                            className="sr-only"
                          />
                          <div className={`w-11 h-6 rounded-full transition-colors ${isEnabled ? 'bg-green-500' : 'bg-gray-300'} ${isOnlyOne ? 'opacity-50' : ''}`}>
                            <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${isEnabled ? 'translate-x-5.5 ml-[22px]' : 'ml-0.5'}`} />
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Event Dialog */}
      <EventDialog 
        open={eventDialog.open} 
        event={eventDialog.event} 
        onClose={() => setEventDialog({ open: false, event: null })}
        onSave={handleSaveEvent}
      />

      {/* Gallery Dialog */}
      <GalleryImageDialog 
        open={galleryDialog.open} 
        image={galleryDialog.image} 
        onClose={() => setGalleryDialog({ open: false, image: null })}
        onSave={handleSaveGalleryImage}
      />

      {/* Video Dialog */}
      <VideoDialog 
        open={videoDialog.open} 
        video={videoDialog.video} 
        onClose={() => setVideoDialog({ open: false, video: null })}
        onSave={handleSaveVideo}
      />

      {/* Package Dialog */}
      <PackageDialog 
        open={packageDialog.open} 
        pkg={packageDialog.pkg} 
        onClose={() => setPackageDialog({ open: false, pkg: null })}
        onSave={handleSavePackage}
      />

      {/* Partner Dialog */}
      <PartnerDialog 
        open={partnerDialog.open} 
        partner={partnerDialog.partner} 
        onClose={() => setPartnerDialog({ open: false, partner: null })}
        onSave={handleSavePartner}
      />
    </div>
  );
};

// Event Dialog Component
const EventDialog = ({ open, event, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    year: '', location: '', title: '', countries: 0, attendees: 0, image: ''
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData(event);
    } else {
      setFormData({ year: '', location: '', title: '', countries: 0, attendees: 0, image: '' });
    }
  }, [event, open]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = sessionStorage.getItem('adminToken');
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      setFormData({ ...formData, image: res.data.url });
      toast.success('Image uploaded!');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{event ? 'Edit Event' : 'Add Event'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Year</Label><Input value={formData.year} onChange={(e) => setFormData({...formData, year: e.target.value})} placeholder="2024" /></div>
            <div><Label>Location</Label><Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} placeholder="Singapore" /></div>
          </div>
          <div><Label>Event Title</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Asian Business Forum 2025" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Countries</Label><Input type="number" value={formData.countries} onChange={(e) => setFormData({...formData, countries: parseInt(e.target.value) || 0})} /></div>
            <div><Label>Attendees</Label><Input type="number" value={formData.attendees} onChange={(e) => setFormData({...formData, attendees: parseInt(e.target.value) || 0})} /></div>
          </div>
          <div>
            <Label>Background Image</Label>
            <label className="block mt-1 cursor-pointer">
              <div className="border-2 border-dashed rounded-lg h-32 flex items-center justify-center overflow-hidden">
                {uploading ? <div className="animate-spin h-6 w-6 border-2 border-green-600 border-t-transparent rounded-full"></div> :
                  formData.image ? <img src={formData.image} alt="Preview" className="w-full h-full object-cover" /> :
                  <span className="text-gray-400">Click to upload</span>}
              </div>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} className="bg-green-600 hover:bg-green-700">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Gallery Image Dialog Component
const GalleryImageDialog = ({ open, image, onClose, onSave }) => {
  const [formData, setFormData] = useState({ src: '', alt: '', caption: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (image) {
      setFormData(image);
    } else {
      setFormData({ src: '', alt: '', caption: '' });
    }
  }, [image, open]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = sessionStorage.getItem('adminToken');
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      setFormData({ ...formData, src: res.data.url });
      toast.success('Image uploaded!');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{image ? 'Edit Gallery Image' : 'Add Gallery Image'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Image</Label>
            <label className="block mt-1 cursor-pointer">
              <div className="border-2 border-dashed rounded-lg h-40 flex items-center justify-center overflow-hidden">
                {uploading ? <div className="animate-spin h-6 w-6 border-2 border-green-600 border-t-transparent rounded-full"></div> :
                  formData.src ? <img src={formData.src} alt="Preview" className="w-full h-full object-cover" /> :
                  <span className="text-gray-400">Click to upload</span>}
              </div>
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          </div>
          <div><Label>Alt Text</Label><Input value={formData.alt} onChange={(e) => setFormData({...formData, alt: e.target.value})} placeholder="Image description" /></div>
          <div><Label>Caption</Label><Input value={formData.caption} onChange={(e) => setFormData({...formData, caption: e.target.value})} placeholder="Caption to display" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} className="bg-green-600 hover:bg-green-700">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Video Dialog Component
const VideoDialog = ({ open, video, onClose, onSave }) => {
  const [formData, setFormData] = useState({ title: '', url: '', type: 'upload', thumbnail: '', description: '', platform: '' });
  const [uploading, setUploading] = useState(false);
  const [videoMode, setVideoMode] = useState('upload'); // 'upload' or 'url'
  const [urlInput, setUrlInput] = useState('');
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    if (video) {
      setFormData(video);
      setVideoMode(video.type === 'upload' ? 'upload' : 'url');
      if (video.type !== 'upload') {
        setUrlInput(video.url || '');
      }
    } else {
      setFormData({ title: '', url: '', type: 'upload', thumbnail: '', description: '', platform: '' });
      setVideoMode('upload');
      setUrlInput('');
    }
  }, [video, open]);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (500MB max)
    if (file.size > 500 * 1024 * 1024) {
      toast.error('Video must be less than 500MB');
      return;
    }
    
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = sessionStorage.getItem('adminToken');
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload-video`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      setFormData({ 
        ...formData, 
        url: res.data.url, 
        thumbnail: res.data.thumbnail || formData.thumbnail,
        type: 'upload' 
      });
      toast.success('Video uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
    finally { setUploading(false); }
  };

  const handleValidateUrl = async () => {
    if (!urlInput) {
      toast.error('Please enter a video URL');
      return;
    }
    
    setValidating(true);
    try {
      const token = sessionStorage.getItem('adminToken');
      const res = await axios.post(`${BACKEND_URL}/api/admin/validate-video-url`, 
        { url: urlInput },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      if (res.data.valid) {
        setFormData({
          ...formData,
          url: res.data.embed_url,
          thumbnail: res.data.thumbnail,
          type: res.data.platform,
          platform: res.data.platform
        });
        toast.success(`${res.data.platform.charAt(0).toUpperCase() + res.data.platform.slice(1)} video validated!`);
      } else {
        toast.error(res.data.error || 'Invalid video URL');
      }
    } catch (error) {
      toast.error('Failed to validate URL');
    }
    finally { setValidating(false); }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = sessionStorage.getItem('adminToken');
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      setFormData({ ...formData, thumbnail: res.data.url });
      toast.success('Thumbnail uploaded!');
    } catch { toast.error('Upload failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{video ? 'Edit Video' : 'Add Video'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="Video title" />
          </div>
          
          {/* Video Source Toggle */}
          <div>
            <Label className="mb-2 block">Video Source</Label>
            <div className="flex gap-2 mb-3">
              <Button 
                type="button"
                variant={videoMode === 'upload' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setVideoMode('upload')}
                className={videoMode === 'upload' ? 'bg-green-600' : ''}
              >
                Upload MP4
              </Button>
              <Button 
                type="button"
                variant={videoMode === 'url' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setVideoMode('url')}
                className={videoMode === 'url' ? 'bg-green-600' : ''}
              >
                YouTube/Vimeo/Drive URL
              </Button>
            </div>
          </div>
          
          {videoMode === 'upload' ? (
            <div>
              <Label>Video File (MP4)</Label>
              <label className="block mt-1 cursor-pointer">
                <div className="border-2 border-dashed rounded-lg p-4 flex items-center justify-center">
                  {uploading ? <div className="animate-spin h-6 w-6 border-2 border-green-600 border-t-transparent rounded-full"></div> :
                    formData.url && formData.type === 'upload' ? <span className="text-green-600 text-sm">✓ Video uploaded</span> :
                    <span className="text-gray-400">Click to upload MP4 (max 500MB)</span>}
                </div>
                <input type="file" accept="video/mp4,video/*" onChange={handleVideoUpload} className="hidden" />
              </label>
              {formData.url && formData.type === 'upload' && (
                <a href={formData.url} target="_blank" rel="noreferrer" className="text-xs text-blue-500">View video</a>
              )}
            </div>
          ) : (
            <div>
              <Label>Video URL (YouTube, Vimeo, or Google Drive)</Label>
              <div className="flex gap-2 mt-1">
                <Input 
                  value={urlInput} 
                  onChange={(e) => setUrlInput(e.target.value)} 
                  placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                  className="flex-1"
                />
                <Button 
                  type="button"
                  onClick={handleValidateUrl}
                  disabled={validating}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {validating ? 'Checking...' : 'Validate'}
                </Button>
              </div>
              {formData.platform && (
                <p className="text-xs text-green-600 mt-1">✓ {formData.platform.charAt(0).toUpperCase() + formData.platform.slice(1)} video validated</p>
              )}
            </div>
          )}
          
          <div>
            <Label>Thumbnail {videoMode === 'url' ? '(auto-generated, or upload custom)' : '(optional)'}</Label>
            <label className="block mt-1 cursor-pointer">
              <div className="border-2 border-dashed rounded-lg h-24 flex items-center justify-center overflow-hidden">
                {formData.thumbnail ? (
                  <img src={formData.thumbnail} alt="Thumb" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                ) : (
                  <span className="text-gray-400 text-sm">Click to upload thumbnail</span>
                )}
              </div>
              <input type="file" accept="image/*" onChange={handleThumbnailUpload} className="hidden" />
            </label>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Short description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} className="bg-green-600 hover:bg-green-700">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Package Dialog Component
const PackageDialog = ({ open, pkg, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    type: 'delegate',
    title: { en: '', tr: '', id: '' },
    description: { en: '', tr: '', id: '' },
    features: [],
    ctaText: { en: 'Contact Us', tr: 'Bize Ulaşın', id: 'Hubungi Kami' },
    featured: false
  });
  const [newFeature, setNewFeature] = useState({ en: '', tr: '', id: '' });

  useEffect(() => {
    if (pkg) {
      setFormData({
        type: pkg.type || 'delegate',
        title: pkg.title || { en: '', tr: '', id: '' },
        description: pkg.description || { en: '', tr: '', id: '' },
        features: pkg.features || [],
        ctaText: pkg.ctaText || { en: 'Contact Us', tr: 'Bize Ulaşın', id: 'Hubungi Kami' },
        featured: pkg.featured || false
      });
    } else {
      setFormData({
        type: 'delegate',
        title: { en: '', tr: '', id: '' },
        description: { en: '', tr: '', id: '' },
        features: [],
        ctaText: { en: 'Contact Us', tr: 'Bize Ulaşın', id: 'Hubungi Kami' },
        featured: false
      });
    }
  }, [pkg, open]);

  const addFeature = () => {
    if (newFeature.en) {
      setFormData({ ...formData, features: [...formData.features, { ...newFeature }] });
      setNewFeature({ en: '', tr: '', id: '' });
    }
  };

  const removeFeature = (idx) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== idx) });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{pkg ? 'Edit Package' : 'Add Package'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Package Type</Label>
              <select 
                value={formData.type} 
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="w-full mt-1 border rounded-md p-2"
              >
                <option value="delegate">Delegate</option>
                <option value="booth">Booth</option>
                <option value="sponsorship">Sponsorship</option>
              </select>
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input 
                type="checkbox" 
                checked={formData.featured} 
                onChange={(e) => setFormData({...formData, featured: e.target.checked})}
                className="w-4 h-4"
              />
              <Label>Featured (Popular badge)</Label>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">Title</h4>
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="English" value={formData.title.en} onChange={(e) => setFormData({...formData, title: {...formData.title, en: e.target.value}})} />
              <Input placeholder="Turkish (TR)" value={formData.title.tr} onChange={(e) => setFormData({...formData, title: {...formData.title, tr: e.target.value}})} />
              <Input placeholder="Indonesian (ID)" value={formData.title.id} onChange={(e) => setFormData({...formData, title: {...formData.title, id: e.target.value}})} />
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">Description</h4>
            <div className="space-y-2">
              <Textarea placeholder="English description" value={formData.description.en} onChange={(e) => setFormData({...formData, description: {...formData.description, en: e.target.value}})} rows={2} />
              <Textarea placeholder="Turkish (TR) description" value={formData.description.tr} onChange={(e) => setFormData({...formData, description: {...formData.description, tr: e.target.value}})} rows={2} />
              <Textarea placeholder="Indonesian (ID) description" value={formData.description.id} onChange={(e) => setFormData({...formData, description: {...formData.description, id: e.target.value}})} rows={2} />
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">Features ({formData.features.length})</h4>
            <div className="space-y-2 mb-3">
              {formData.features.map((f, idx) => (
                <div key={`feature-${idx}-${f.en || f}`} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                  <span className="flex-1 text-sm">{f.en || f}</span>
                  <button onClick={() => removeFeature(idx)} className="text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input placeholder="New feature (EN)" value={newFeature.en} onChange={(e) => setNewFeature({...newFeature, en: e.target.value})} className="flex-1" />
              <Button type="button" onClick={addFeature} size="sm" variant="outline"><Plus size={16} /></Button>
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">Button Text</h4>
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="English" value={formData.ctaText.en} onChange={(e) => setFormData({...formData, ctaText: {...formData.ctaText, en: e.target.value}})} />
              <Input placeholder="Turkish (TR)" value={formData.ctaText.tr} onChange={(e) => setFormData({...formData, ctaText: {...formData.ctaText, tr: e.target.value}})} />
              <Input placeholder="Indonesian (ID)" value={formData.ctaText.id} onChange={(e) => setFormData({...formData, ctaText: {...formData.ctaText, id: e.target.value}})} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} className="bg-green-600 hover:bg-green-700">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Partner Dialog Component
const PartnerDialog = ({ open, partner, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '', logo: '', category: 'Government', url: '', visible: true, order: 0
  });
  const [uploading, setUploading] = useState(false);

  const categories = ['Government', 'Associations', 'Media', 'Corporate'];

  useEffect(() => {
    if (partner) {
      setFormData({
        name: partner.name || '',
        logo: partner.logo || '',
        category: partner.category || 'Government',
        url: partner.url || '',
        visible: partner.visible !== false,
        order: partner.order || 0
      });
    } else {
      setFormData({ name: '', logo: '', category: 'Government', url: '', visible: true, order: 0 });
    }
  }, [partner, open]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const token = sessionStorage.getItem('adminToken');
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
      });
      setFormData({ ...formData, logo: res.data.url });
      toast.success('Logo uploaded!');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{partner ? 'Edit Partner' : 'Add Partner'}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Partner Name</Label>
            <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Partner Organization Name" />
          </div>
          <div>
            <Label>Category</Label>
            <select 
              value={formData.category} 
              onChange={(e) => setFormData({...formData, category: e.target.value})}
              className="w-full border rounded-md px-3 py-2 mt-1"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Website URL (optional)</Label>
            <Input value={formData.url} onChange={(e) => setFormData({...formData, url: e.target.value})} placeholder="https://example.com" />
          </div>
          <div>
            <Label>Logo Image</Label>
            <label className="block mt-1 cursor-pointer">
              <div className="border-2 border-dashed rounded-lg h-32 flex items-center justify-center overflow-hidden bg-gray-50">
                {uploading ? <div className="animate-spin h-6 w-6 border-2 border-green-600 border-t-transparent rounded-full"></div> :
                  formData.logo ? <img src={formData.logo} alt="Preview" className="max-w-full max-h-full object-contain" /> :
                  <div className="text-center text-gray-400">
                    <Upload size={24} className="mx-auto mb-1" />
                    <span className="text-xs">Click to upload logo</span>
                  </div>}
              </div>
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            </label>
            {formData.logo && (
              <button onClick={() => setFormData({...formData, logo: ''})} className="text-xs text-red-500 mt-1 hover:underline">Remove</button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input 
              type="checkbox" 
              checked={formData.visible} 
              onChange={(e) => setFormData({...formData, visible: e.target.checked})}
              className="rounded border-gray-300"
            />
            <Label className="!mt-0">Visible on website</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(formData)} className="bg-green-600 hover:bg-green-700">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};